/**
 * Weather & seasons — the first system of every hour (docs/Weather.md).
 *
 *  - Season from the config (or the seed) sets the climate: temperature, fronts, convective
 *    storms, fog and the ground at the start.
 *  - Moving weather cells (fronts across the wind, round afternoon thunderstorms) drift with a
 *    wandering prevailing wind; per hex precipitation = the strongest cell there.
 *  - The ground keeps a memory: wetness (rain, snowmelt; dries by terrain, warmth and sun),
 *    snow depth and frost per hex, so mud, snow cover and frozen rivers/marsh follow the weather
 *    of the last days, not the last hour.
 *  - Morning fog 05–09 in low, wet ground on foggy mornings (drawn daily), lifting hour by hour.
 *  - Every FORECAST_INTERVAL hours the Meteorological Service issues a 24 h regional outlook
 *    from an ensemble of perturbed projections — its uncertainty grows honestly with lead time.
 *  - Teletype: MET warnings for a faction's front (rate-limited) and public ground notices
 *    (first frost, thaw, mud season, firm ground, snow) with hysteresis.
 *
 * Weather has its own RNG stream in the state and never reads units or commands, so it is a
 * pure function of (seed, config, tick): replays reproduce it exactly. The rules that feel the
 * weather live with their systems and read `state.weather.conditions` via core/weather.ts.
 */
import {
  ANOMALY_NOISE,
  ANOMALY_PERSISTENCE,
  CLIMATE,
  CLOUD_MIN,
  DRY_RATE,
  FOG_DRAW_HOUR,
  FOG_START_HOUR,
  FOG_THRESHOLD,
  FORECAST_DRIFT_ERROR,
  FORECAST_INTERVAL,
  FORECAST_LEADS,
  FORECAST_MEMBERS,
  FORECAST_SAMPLES,
  FREEZE_RATE,
  FROST_MAX,
  HOURS_PER_DAY,
  MELT_RATE,
  MET_FOG_CHANCE,
  MET_INTERVAL,
  MET_PRECIP_CHANCE,
  MET_SEVERE_CHANCE,
  MUD_MIN,
  RAIN_MIN,
  REGIME_SHARES,
  type SeasonClimate,
  SNOW_COVER_MIN,
  SNOW_GAIN,
  SNOW_TEMP,
  START_HOUR,
  STORM_MIN,
  TERRAIN_DRYING,
  THAW_RATE,
  WET_GAIN,
  WET_MIN,
} from '../core/constants';
import { HexGrid, type HexId, NO_HEX } from '../core/hex';
import { Rng } from '../core/rng';
import {
  type FactionId,
  type ForecastCondition,
  type GameMap,
  isWater,
  NO_FACTION,
  SEASONS,
  type Season,
  type SessionConfig,
  Terrain,
  type WeatherCell,
  type WeatherChange,
  type WeatherForecast,
  type WeatherState,
} from '../core/types';
import {
  cellIntensity,
  cellPrecipAt,
  diurnalCurve,
  hexTemperature,
  W_FOG,
  W_FROZEN,
  W_MUD,
  W_RAIN,
  W_SNOW,
  W_SNOWING,
  W_STORM,
  W_WET,
  weatherCoords,
} from '../core/weather';
import { emit, type SimContext } from './context';

/** Spread of the per-hex daily fog potential (dayNoise), ± half of it. */
const FOG_NOISE = 0.15;
/** Hours of spin-up before tick 0, so a match opens on a lived-in sky and ground. */
const SPIN_UP_HOURS = 36;
const SQRT3 = Math.sqrt(3);

/** Hour of day for any tick, including the negative ticks of the spin-up. */
const hourAt = (tick: number): number =>
  (((tick + START_HOUR) % HOURS_PER_DAY) + HOURS_PER_DAY) % HOURS_PER_DAY;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

// ---- per-map static data --------------------------------------------------------------------

interface MapInfo {
  grid: HexGrid;
  coords: Float64Array;
  land: Uint8Array;
  landCount: number;
  /** 0..1: how low-lying a hex is (fog, initial wetness). */
  lowland: Float32Array;
  /** Static part of the fog potential (low ground, water nearby). */
  fogBase: Float32Array;
  /** Terrain drainage factor. */
  drying: Float32Array;
  /** Plains, forest, hills: ground that can turn to mud. */
  mudCapable: Uint8Array;
  /** Per region: the hexes the forecast samples (anchor first, then spread out). */
  samples: Int32Array[];
  /** Per region: mean elevation of the samples (snow line). */
  regionElevation: Float32Array;
  /** Per region: bounding box of its samples in weather units [x0, y0, x1, y1] (cheap rejects). */
  regionBox: Float64Array;
  /** Per hex: degrees colder than sea level (lapse rate × elevation). */
  lapse: Float32Array;
  /** Bounds of the land in weather units. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const infoCache = new WeakMap<GameMap, MapInfo>();

function mapInfo(map: GameMap, grid: HexGrid): MapInfo {
  const cached = infoCache.get(map);
  if (cached && cached.grid.count === grid.count) return cached;
  const n = grid.count;
  const coords = weatherCoords(grid);
  const land = new Uint8Array(n);
  const lowland = new Float32Array(n);
  const fogBase = new Float32Array(n);
  const drying = new Float32Array(n);
  const mudCapable = new Uint8Array(n);
  let landCount = 0;
  let x0 = Number.POSITIVE_INFINITY;
  let y0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let y1 = Number.NEGATIVE_INFINITY;
  for (let h = 0; h < n; h++) {
    const t = map.terrain[h];
    if (isWater(t)) continue;
    land[h] = 1;
    landCount++;
    lowland[h] = clamp01((110 - map.elevation[h]) / 60);
    let waterside = t === Terrain.Marsh || map.riverEdges[h] !== 0 ? 1 : 0;
    for (let d = 0; d < 6 && !waterside; d++) {
      const nb = grid.neighbour(h, d);
      if (nb >= 0 && isWater(map.terrain[nb])) waterside = 1;
    }
    fogBase[h] = t === Terrain.Mountains ? -1 : 0.3 * lowland[h] + 0.2 * waterside;
    drying[h] = TERRAIN_DRYING[t] ?? 1;
    mudCapable[h] = t === Terrain.Plains || t === Terrain.Forest || t === Terrain.Hills ? 1 : 0;
    const x = coords[h * 2];
    const y = coords[h * 2 + 1];
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  if (landCount === 0) {
    x0 = 0;
    y0 = 0;
    x1 = map.width;
    y1 = map.height;
  }
  const { samples, regionElevation } = regionSamples(map, grid);
  const regionBox = new Float64Array(samples.length * 4);
  for (let r = 0; r < samples.length; r++) {
    regionBox.set([Infinity, Infinity, -Infinity, -Infinity], r * 4);
    for (const h of samples[r]) {
      regionBox[r * 4] = Math.min(regionBox[r * 4], coords[h * 2]);
      regionBox[r * 4 + 1] = Math.min(regionBox[r * 4 + 1], coords[h * 2 + 1]);
      regionBox[r * 4 + 2] = Math.max(regionBox[r * 4 + 2], coords[h * 2]);
      regionBox[r * 4 + 3] = Math.max(regionBox[r * 4 + 3], coords[h * 2 + 1]);
    }
  }
  const lapse = new Float32Array(n);
  for (let h = 0; h < n; h++) lapse[h] = -hexTemperature(0, map.elevation[h]);
  const info: MapInfo = {
    grid,
    coords,
    land,
    landCount,
    lowland,
    fogBase,
    drying,
    mudCapable,
    samples,
    regionElevation,
    regionBox,
    lapse,
    x0,
    y0,
    x1,
    y1,
  };
  infoCache.set(map, info);
  return info;
}

/** Forecast sample hexes per region: its label anchor, then farthest-point spread. */
function regionSamples(
  map: GameMap,
  grid: HexGrid,
): { samples: Int32Array[]; regionElevation: Float32Array } {
  const members: number[][] = map.regions.map(() => []);
  for (let h = 0; h < grid.count; h++) {
    const r = map.region[h];
    if (r >= 0 && r < members.length) members[r].push(h);
  }
  const samples: Int32Array[] = [];
  const regionElevation = new Float32Array(map.regions.length);
  for (let r = 0; r < map.regions.length; r++) {
    const list = members[r];
    const chosen: number[] = [];
    const anchor = map.regions[r].hex;
    if (list.includes(anchor)) chosen.push(anchor);
    else if (list.length) chosen.push(list[0]);
    while (chosen.length < Math.min(FORECAST_SAMPLES, list.length)) {
      let best = -1;
      let bestGap = -1;
      for (const h of list) {
        let gap = Number.POSITIVE_INFINITY;
        for (const c of chosen) gap = Math.min(gap, grid.distance(c, h));
        if (gap > bestGap) {
          bestGap = gap;
          best = h;
        }
      }
      if (bestGap <= 0) break;
      chosen.push(best);
    }
    samples.push(Int32Array.from(chosen));
    let e = 0;
    for (const h of chosen) e += map.elevation[h];
    regionElevation[r] = chosen.length ? e / chosen.length : 100;
  }
  return { samples, regionElevation };
}

/** Deterministic per-hex, per-day noise in [0, 1) (integer hash; no RNG draw). */
function dayNoise(h: HexId, day: number): number {
  let x = Math.imul(h + 1, 0x27d4eb2d) ^ Math.imul(day + 7, 0x165667b1);
  x = Math.imul(x ^ (x >>> 15), 0x85ebca6b);
  x ^= x >>> 13;
  return (x >>> 0) / 4294967296;
}

// ---- setup ----------------------------------------------------------------------------------

/** The campaign's season: the config's, else one drawn from the seed. */
export function seasonOf(config: Pick<SessionConfig, 'seed' | 'season'>): Season {
  if (config.season && SEASONS.includes(config.season)) return config.season;
  return SEASONS[new Rng((config.seed ^ 0x5ea5011d) >>> 0).int(0, 3)];
}

function emptyForecast(regions: number): WeatherForecast {
  const size = regions * FORECAST_LEADS.length;
  return {
    issuedAt: 0,
    leads: [...FORECAST_LEADS],
    precip: new Uint8Array(size),
    severe: new Uint8Array(size),
    snow: new Uint8Array(size),
    fog: new Uint8Array(regions),
    tempMin: 0,
    tempMax: 0,
  };
}

/**
 * Weather at tick 0. `live` = false gives static skies (tests, scenarios): no cells, dry ground,
 * nothing ever changes unless a test writes `conditions` by hand.
 */
export function initWeather(
  config: Pick<SessionConfig, 'seed' | 'season'>,
  map: GameMap,
  grid: HexGrid,
  factions: number,
  live: boolean,
): WeatherState {
  const season = seasonOf(config);
  const climate = CLIMATE[season];
  const n = grid.count;
  const rng = new Rng((config.seed ^ 0x7ea7e3b5) >>> 0);
  const ws: WeatherState = {
    season,
    live,
    rngState: rng.getState(),
    nextCellId: 1,
    wind: { x: climate.wind, y: 0 },
    anomaly: 0,
    temperature: climate.temp,
    cells: [],
    fogStrength: 0,
    wetness: new Float32Array(n),
    snow: new Float32Array(n),
    frost: new Float32Array(n),
    conditions: new Uint8Array(n),
    forecast: emptyForecast(map.regions.length),
    regime: { frozen: false, mud: false, snow: false },
    metAt: new Array<number>(factions).fill(-MET_INTERVAL * 4),
    metKey: new Array<string>(factions).fill(''),
  };
  if (!live) return ws;
  const info = mapInfo(map, grid);

  // Prevailing wind: westerly, within about ±35°.
  const t = rng.range(-0.7, 0.7);
  const speed = climate.wind * rng.range(0.85, 1.15);
  const norm = Math.sqrt(1 + t * t);
  ws.wind = { x: speed / norm, y: (speed * t) / norm };
  ws.anomaly = Math.max(-5, Math.min(5, rng.gaussian() * 2.2));

  // The ground as the preceding weeks left it; the spin-up then adds recent weather on top.
  for (let h = 0; h < n; h++) {
    if (!info.land[h]) continue;
    const air = hexTemperature(climate.temp + ws.anomaly, map.elevation[h]);
    const moist = (map.moisture[h] - 128) / 128;
    ws.wetness[h] = clamp01(
      climate.wetStart + 0.28 * info.lowland[h] + 0.15 * moist - 0.1 * (1 - info.lowland[h]),
    );
    if (map.terrain[h] === Terrain.Marsh) ws.wetness[h] = Math.max(ws.wetness[h], 0.5);
    if (climate.snowStart > 0 || air < -1) ws.snow[h] = clamp01(climate.snowStart + (air < -1 ? 0.2 : 0));
    if (climate.frozenStart || air < -3) ws.frost[h] = FROST_MAX * 0.85;
  }
  // Systems already on the map, at random stages of their lives.
  const expected = (climate.fronts * n) / 1000;
  const count = Math.round(expected * rng.range(0.6, 1.4));
  for (let i = 0; i < count; i++) {
    const c = spawnFront(ws, info, climate, rng, true);
    c.age = rng.range(0, c.life * 0.8);
  }
  ws.rngState = rng.getState();
  let shares: Shares = { frozen: 0, mud: 0, snow: 0 };
  for (let tick = -SPIN_UP_HOURS; tick <= 0; tick++) shares = advance(ws, info, map, climate, tick);
  ws.regime = {
    frozen: shares.frozen >= REGIME_SHARES.frozen[0],
    mud: shares.mud >= REGIME_SHARES.mud[0],
    snow: shares.snow >= REGIME_SHARES.snow[0],
  };
  ws.forecast = computeForecast(ws, info, map, climate, config.seed, 0);
  return ws;
}

// ---- cells ----------------------------------------------------------------------------------

/** A frontal system: long across the wind. Arrives from upwind or forms over the map. */
function spawnFront(ws: WeatherState, info: MapInfo, climate: SeasonClimate, rng: Rng, anywhere: boolean) {
  const w = ws.wind;
  const speed = Math.sqrt(w.x * w.x + w.y * w.y) || 1;
  const major = rng.range(climate.frontMajor[0], climate.frontMajor[1]);
  const minor = rng.range(climate.frontMinor[0], climate.frontMinor[1]);
  let x: number;
  let y = rng.range(info.y0 - major * 0.3, info.y1 + major * 0.3);
  if (anywhere || rng.chance(0.45)) x = rng.range(info.x0, info.x1);
  else x = w.x >= 0 ? info.x0 - minor * 0.8 : info.x1 + minor * 0.8;
  if (!anywhere && Math.abs(w.y) > Math.abs(w.x)) {
    // A northerly/southerly flow brings its systems over the top or bottom edge.
    x = rng.range(info.x0, info.x1);
    y = w.y >= 0 ? info.y0 - minor * 0.8 : info.y1 + minor * 0.8;
  }
  const k = rng.range(0.8, 1.2);
  const cell: WeatherCell = {
    id: ws.nextCellId++,
    x,
    y,
    ux: -w.y / speed,
    uy: w.x / speed,
    major,
    minor,
    vx: w.x * k,
    vy: w.y * k,
    peak: rng.range(climate.frontPeak[0], climate.frontPeak[1]),
    age: 0,
    life: rng.range(climate.frontLife[0], climate.frontLife[1]),
  };
  ws.cells.push(cell);
  return cell;
}

/** An afternoon thunderstorm: small, round, violent, short-lived, over land. */
function spawnConvective(ws: WeatherState, info: MapInfo, climate: SeasonClimate, rng: Rng): void {
  const n = info.grid.count;
  let h = rng.int(0, n - 1);
  for (let tries = 0; tries < 8 && !info.land[h]; tries++) h = rng.int(0, n - 1);
  const r = rng.range(climate.convRadius[0], climate.convRadius[1]);
  const k = rng.range(0.5, 0.9);
  ws.cells.push({
    id: ws.nextCellId++,
    x: info.coords[h * 2],
    y: info.coords[h * 2 + 1],
    ux: 1,
    uy: 0,
    major: r,
    minor: r,
    vx: ws.wind.x * k,
    vy: ws.wind.y * k,
    peak: rng.range(climate.convPeak[0], climate.convPeak[1]),
    age: 0,
    life: rng.range(climate.convLife[0], climate.convLife[1]),
  });
}

/** Expected new fronts per hour so that about `climate.fronts` per 1000 hexes are alive. */
function frontRate(climate: SeasonClimate, hexes: number): number {
  const meanLife = (climate.frontLife[0] + climate.frontLife[1]) / 2;
  return (climate.fronts * hexes) / 1000 / meanLife;
}

function moveCells(ws: WeatherState, info: MapInfo, climate: SeasonClimate, rng: Rng, tick: number): void {
  const w = ws.wind;
  const kept: WeatherCell[] = [];
  for (const c of ws.cells) {
    c.x += c.vx;
    c.y += c.vy;
    c.age += 1;
    c.vx += (w.x - c.vx) * 0.03 + rng.gaussian() * 0.01;
    c.vy += (w.y - c.vy) * 0.03 + rng.gaussian() * 0.01;
    const r = c.major > c.minor ? c.major : c.minor;
    const gone =
      c.age >= c.life ||
      c.x - r > info.x1 + 2 ||
      c.x + r < info.x0 - 2 ||
      c.y - r > info.y1 + 2 ||
      c.y + r < info.y0 - 2;
    if (!gone) kept.push(c);
  }
  ws.cells = kept;
  const hexes = info.grid.count;
  const cap = Math.ceil((climate.fronts * hexes * 2.5) / 1000) + 1;
  let fronts = 0;
  for (const c of ws.cells) if (c.major !== c.minor) fronts++;
  if (rng.chance(frontRate(climate, hexes)) && fronts < cap) spawnFront(ws, info, climate, rng, false);
  const hour = hourAt(tick);
  if (climate.convective > 0 && hour >= 12 && hour <= 17 && rng.chance((climate.convective * hexes) / 1000))
    spawnConvective(ws, info, climate, rng);
}

/** The prevailing wind wanders slowly and is pulled back towards the season's westerly flow. */
function turnWind(ws: WeatherState, climate: SeasonClimate, rng: Rng): void {
  const w = ws.wind;
  const e = rng.gaussian() * 0.025;
  let x = w.x - e * w.y;
  let y = w.y + e * w.x;
  y -= y * 0.01;
  if (x < 0.2 * climate.wind) x += 0.01 * climate.wind;
  const s = Math.sqrt(x * x + y * y) || 1;
  const target = s + (climate.wind - s) * 0.02 + rng.gaussian() * 0.004;
  ws.wind = { x: (x / s) * target, y: (y / s) * target };
}

// ---- hourly model ---------------------------------------------------------------------------

let precipScratch: Float64Array | null = null;
/** Carries no state between calls: loaded from and saved to `WeatherState.rngState` each hour. */
const weatherRng = new Rng(0);

/** Per-hex precipitation from the cells (bounding-box scan per cell). */
function rasterPrecip(ws: WeatherState, info: MapInfo): Float64Array {
  const { grid, coords } = info;
  if (!precipScratch || precipScratch.length !== grid.count) precipScratch = new Float64Array(grid.count);
  const p = precipScratch;
  p.fill(0);
  for (const c of ws.cells) {
    const I = cellIntensity(c);
    if (I <= 0.005) continue;
    const r = (c.major > c.minor ? c.major : c.minor) + 1;
    const rowLo = Math.max(0, Math.floor(((c.y - r) * SQRT3 - 1) / 1.5));
    const rowHi = Math.min(grid.height - 1, Math.ceil(((c.y + r) * SQRT3 - 1) / 1.5));
    const colLo = Math.max(0, Math.floor(c.x - r - 1));
    const colHi = Math.min(grid.width - 1, Math.ceil(c.x + r));
    for (let row = rowLo; row <= rowHi; row++) {
      for (let col = colLo; col <= colHi; col++) {
        const h = row * grid.width + col;
        const v = cellPrecipAt(c, I, coords[h * 2], coords[h * 2 + 1]);
        if (v > p[h]) p[h] = v > 1 ? 1 : v;
      }
    }
  }
  return p;
}

/**
 * One hour of weather at `tick`: temperature, wind, cells, ground, fog and the condition bits.
 * Draws only from the weather RNG in `ws`.
 */
function advance(
  ws: WeatherState,
  info: MapInfo,
  map: GameMap,
  climate: SeasonClimate,
  tick: number,
): Shares {
  const rng = weatherRng;
  rng.setState(ws.rngState);
  const hour = hourAt(tick);
  ws.anomaly = ws.anomaly * ANOMALY_PERSISTENCE + rng.gaussian() * ANOMALY_NOISE;
  ws.temperature = seasonalMean(climate, tick) + ws.anomaly + climate.diurnal * diurnalCurve(hour);
  turnWind(ws, climate, rng);
  moveCells(ws, info, climate, rng, tick);
  if (hour === FOG_DRAW_HOUR)
    ws.fogStrength = rng.chance(climate.fogChance) ? rng.range(0.55, 1) : rng.range(0, 0.3);
  ws.rngState = rng.getState();

  const precip = rasterPrecip(ws, info);
  const fogHour = hour - FOG_START_HOUR;
  const fogLimit = fogHour >= 0 && fogHour < FOG_THRESHOLD.length ? FOG_THRESHOLD[fogHour] : -1;
  const day = Math.floor((tick + START_HOUR) / HOURS_PER_DAY);
  const sun = hour >= 7 && hour < 19 ? 1.3 : 0.6;
  const { wetness, snow, frost, conditions } = ws;
  let frozenCount = 0;
  let mudCount = 0;
  let snowCount = 0;
  for (let h = 0; h < info.grid.count; h++) {
    if (!info.land[h]) continue;
    const t = ws.temperature - info.lapse[h];
    const p = precip[h];
    const cold = t <= SNOW_TEMP;
    let wet = wetness[h];
    let sd = snow[h];
    if (p > 0) {
      if (cold) sd += SNOW_GAIN * p;
      else wet += WET_GAIN * p;
    }
    if (sd > 0 && t > 0) {
      const m = Math.min(sd, MELT_RATE * t);
      sd -= m;
      wet += m;
    }
    let fr = frost[h];
    fr = t < 0 ? Math.min(FROST_MAX, fr - FREEZE_RATE * t) : Math.max(0, fr - THAW_RATE * t);
    const wasFrozen = (conditions[h] & W_FROZEN) !== 0;
    const frozen = fr >= 1 || (wasFrozen && fr > 0.5);
    if (p < CLOUD_MIN) {
      const warmth = Math.min(1.6, Math.max(0.1, (t + 5) / 20));
      // Soaked ground drains and evaporates faster than damp ground.
      wet -= DRY_RATE * info.drying[h] * warmth * sun * (0.6 + wet) * (frozen ? 0.15 : 1);
    }
    if (map.terrain[h] === Terrain.Marsh && wet < 0.5) wet = 0.5;
    wet = clamp01(wet);
    sd = clamp01(sd);
    wetness[h] = wet;
    snow[h] = sd;
    frost[h] = fr;

    let c = 0;
    if (p >= RAIN_MIN) {
      c |= W_RAIN;
      if (p >= STORM_MIN) c |= W_STORM;
      if (cold) c |= W_SNOWING;
    }
    const covered = sd >= SNOW_COVER_MIN;
    if (frozen) {
      c |= W_FROZEN;
      frozenCount++;
    }
    if (covered) {
      c |= W_SNOW;
      snowCount++;
    }
    if (!frozen && !covered && wet >= WET_MIN) {
      c |= W_WET;
      if (wet >= MUD_MIN && info.mudCapable[h]) {
        c |= W_MUD;
        mudCount++;
      }
    }
    // Radiation fog: low, wet ground under a clear sky.
    if (fogLimit > 0 && p < CLOUD_MIN && info.fogBase[h] >= 0) {
      const potential = info.fogBase[h] + 0.5 * wet + FOG_NOISE * (dayNoise(h, day) - 0.5);
      if (potential * ws.fogStrength >= fogLimit) c |= W_FOG;
    }
    conditions[h] = c;
  }
  const land = Math.max(1, info.landCount);
  return { frozen: frozenCount / land, mud: mudCount / land, snow: snowCount / land };
}

/** Seasonal mean sea-level temperature at a tick (start value + trend). */
function seasonalMean(climate: SeasonClimate, tick: number): number {
  return climate.temp + (climate.trend * tick) / HOURS_PER_DAY;
}

/** Shares of land frozen, in mud, under snow (the public ground notices). */
interface Shares {
  frozen: number;
  mud: number;
  snow: number;
}

// ---- forecast -------------------------------------------------------------------------------

type MemberCell = Omit<WeatherCell, 'id'>;

/**
 * The Meteorological Service's outlook: FORECAST_MEMBERS perturbed projections of today's cells
 * (drift error, intensity error, a noisy guess at how long each system lives) plus systems that
 * may still form, stepped through the lead times; per region the share of members with
 * precipitation / a storm / snow at any of its sample hexes. Morning fog is climatology applied
 * to today's wet ground. A separate RNG (from seed and tick) — the real future is never read.
 */
function computeForecast(
  ws: WeatherState,
  info: MapInfo,
  map: GameMap,
  climate: SeasonClimate,
  seed: number,
  tick: number,
): WeatherForecast {
  const regions = map.regions.length;
  const steps = FORECAST_LEADS.length;
  const out = emptyForecast(regions);
  out.issuedAt = tick;
  const precipHits = new Uint16Array(regions * steps);
  const severeHits = new Uint16Array(regions * steps);
  const snowHits = new Uint16Array(regions * steps);
  const rng = new Rng((seed ^ Math.imul(tick + 1, 0x2c1b3c6d) ^ 0x6d2b79f5) >>> 0);
  const hexes = info.grid.count;
  const rate = frontRate(climate, hexes);
  const w = ws.wind;
  const wSpeed = Math.sqrt(w.x * w.x + w.y * w.y) || 1;
  for (let m = 0; m < FORECAST_MEMBERS; m++) {
    // One object shape for observed and imagined cells keeps the hot loop monomorphic.
    const cells: MemberCell[] = ws.cells.map((c) => ({
      x: c.x,
      y: c.y,
      ux: c.ux,
      uy: c.uy,
      major: c.major,
      minor: c.minor,
      vx: c.vx + rng.gaussian() * FORECAST_DRIFT_ERROR,
      vy: c.vy + rng.gaussian() * FORECAST_DRIFT_ERROR,
      peak: c.peak * rng.range(0.85, 1.15),
      age: c.age,
      life: c.age + Math.max(2, (c.life - c.age) * rng.range(0.6, 1.4)),
    }));
    let anomaly = ws.anomaly;
    let lead = 0;
    for (let s = 0; s < steps; s++) {
      const dt = FORECAST_LEADS[s] - lead;
      lead = FORECAST_LEADS[s];
      for (let k = 0; k < dt; k++) anomaly = anomaly * ANOMALY_PERSISTENCE + rng.gaussian() * ANOMALY_NOISE;
      for (const c of cells) {
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        c.age += dt;
      }
      // Systems that may form in the meantime (the forecaster's climatology).
      if (rng.chance(Math.min(0.9, rate * dt))) {
        const major = rng.range(climate.frontMajor[0], climate.frontMajor[1]);
        const minor = rng.range(climate.frontMinor[0], climate.frontMinor[1]);
        const upwind = rng.chance(0.55);
        cells.push({
          x: upwind ? (w.x >= 0 ? info.x0 : info.x1) : rng.range(info.x0, info.x1),
          y: rng.range(info.y0, info.y1),
          ux: -w.y / wSpeed,
          uy: w.x / wSpeed,
          major,
          minor,
          vx: w.x,
          vy: w.y,
          peak: rng.range(climate.frontPeak[0], climate.frontPeak[1]),
          age: dt * 0.5,
          life: rng.range(climate.frontLife[0], climate.frontLife[1]),
        });
      }
      const hour = hourAt(tick + lead);
      let afternoon = 0;
      for (let k = 0; k < dt; k++) {
        const hk = hourAt(tick + lead - k);
        if (hk >= 12 && hk <= 17) afternoon++;
      }
      if (afternoon > 0 && rng.chance(Math.min(0.9, (climate.convective * hexes * afternoon) / 1000))) {
        const h = rng.int(0, hexes - 1);
        const r = rng.range(climate.convRadius[0], climate.convRadius[1]);
        cells.push({
          x: info.coords[h * 2],
          y: info.coords[h * 2 + 1],
          ux: 1,
          uy: 0,
          major: r,
          minor: r,
          vx: w.x * 0.7,
          vy: w.y * 0.7,
          peak: rng.range(climate.convPeak[0], climate.convPeak[1]),
          age: 2,
          life: rng.range(climate.convLife[0], climate.convLife[1]),
        });
      }
      const air = seasonalMean(climate, tick + lead) + anomaly + climate.diurnal * diurnalCurve(hour);
      const strength = cells.map(cellIntensity);
      const box = info.regionBox;
      for (let r = 0; r < regions; r++) {
        let p = 0;
        for (let ci = 0; ci < cells.length; ci++) {
          const c = cells[ci];
          const reach = c.major > c.minor ? c.major : c.minor;
          if (
            strength[ci] <= p ||
            c.x + reach < box[r * 4] ||
            c.x - reach > box[r * 4 + 2] ||
            c.y + reach < box[r * 4 + 1] ||
            c.y - reach > box[r * 4 + 3]
          )
            continue;
          for (const h of info.samples[r]) {
            const v = cellPrecipAt(c, strength[ci], info.coords[h * 2], info.coords[h * 2 + 1]);
            if (v > p) p = v;
          }
          if (p >= STORM_MIN) break;
        }
        if (p < RAIN_MIN) continue;
        const i = r * steps + s;
        precipHits[i]++;
        if (p >= STORM_MIN) severeHits[i]++;
        if (hexTemperature(air, info.regionElevation[r]) <= SNOW_TEMP) snowHits[i]++;
      }
    }
  }
  for (let i = 0; i < regions * steps; i++) {
    out.precip[i] = Math.round((100 * precipHits[i]) / FORECAST_MEMBERS);
    out.severe[i] = Math.round((100 * severeHits[i]) / FORECAST_MEMBERS);
    out.snow[i] = precipHits[i] ? Math.round((100 * snowHits[i]) / precipHits[i]) : 0;
  }
  // Fog next morning: the potential of today's ground × the night's strength S, which is known
  // from FOG_DRAW_HOUR on (evening and midnight bulletins) and climatology before; no fog under
  // cloud, so a likely rainy dawn lowers the chance.
  const hour = hourAt(tick);
  const known = hour >= FOG_DRAW_HOUR || hour < FOG_START_HOUR;
  let dawn = 1;
  while (hourAt(tick + dawn) !== FOG_START_HOUR + 1) dawn++;
  let dawnStep = 0;
  for (let s = 0; s < steps; s++)
    if (Math.abs(FORECAST_LEADS[s] - dawn) < Math.abs(FORECAST_LEADS[dawnStep] - dawn)) dawnStep = s;
  const strengths = known
    ? [[ws.fogStrength, 1]]
    : [0, 1, 2, 3, 4].flatMap((i) => [
        [0.55 + (0.45 * (i + 0.5)) / 5, climate.fogChance / 5],
        [(0.3 * (i + 0.5)) / 5, (1 - climate.fogChance) / 5],
      ]);
  for (let r = 0; r < regions; r++) {
    let best = 0;
    for (const h of info.samples[r]) {
      if (info.fogBase[h] < 0) continue;
      const pot = info.fogBase[h] + 0.5 * ws.wetness[h];
      let chance = 0;
      for (const [strength, weight] of strengths)
        if (strength > 0)
          chance += weight * clamp01((pot + FOG_NOISE / 2 - FOG_THRESHOLD[0] / strength) / FOG_NOISE);
      if (chance > best) best = chance;
    }
    out.fog[r] = Math.round(100 * best * (1 - out.precip[r * steps + dawnStep] / 100));
  }
  // Temperature range over the next 24 h (expected anomaly decays towards the seasonal mean).
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  let decay = 1;
  for (let l = 1; l <= 24; l++) {
    decay *= ANOMALY_PERSISTENCE;
    const t =
      seasonalMean(climate, tick + l) + ws.anomaly * decay + climate.diurnal * diurnalCurve(hourAt(tick + l));
    if (t < lo) lo = t;
    if (t > hi) hi = t;
  }
  out.tempMin = Math.round(lo);
  out.tempMax = Math.round(hi);
  return out;
}

// ---- notices --------------------------------------------------------------------------------

/**
 * Regions along faction f's front as its staff sees it: its own (believed) hexes bordering land
 * it believes hostile. Falls back to the regions its units stand in.
 */
function frontRegions(ctx: SimContext, f: FactionId): Set<number> {
  const { state, map, grid, teamOf } = ctx;
  const known = state.knownOwner[f];
  const team = teamOf[f];
  const out = new Set<number>();
  const nb = grid.neighbours;
  for (let h = 0; h < grid.count; h++) {
    if (known[h] !== f || map.region[h] < 0) continue;
    for (let d = 0; d < 6; d++) {
      const n = nb[h * 6 + d];
      if (n < 0) continue;
      const o = known[n];
      if (o !== NO_FACTION && teamOf[o] !== team) {
        out.add(map.region[h]);
        break;
      }
    }
  }
  if (out.size === 0)
    for (const u of state.units.values())
      if (u.faction === f && map.region[u.hex] >= 0) out.add(map.region[u.hex]);
  return out;
}

interface MetCandidate {
  region: number;
  condition: ForecastCondition;
  lead: number;
  chance: number;
  score: number;
}

/** The most significant coming change over a front region, or null. */
function metCandidate(ctx: SimContext, f: FactionId, info: MapInfo): MetCandidate | null {
  const ws = ctx.state.weather;
  const fc = ws.forecast;
  const steps = fc.leads.length;
  let best: MetCandidate | null = null;
  for (const r of frontRegions(ctx, f)) {
    let now = 0;
    for (const h of info.samples[r]) now |= ws.conditions[h];
    for (let s = 0; s < steps; s++) {
      const i = r * steps + s;
      const snowy = fc.snow[i] >= 50;
      let cond: ForecastCondition | null = null;
      let chance = 0;
      let weight = 0;
      if (fc.severe[i] >= MET_SEVERE_CHANCE && !(now & W_STORM)) {
        cond = snowy ? 'blizzard' : 'storm';
        chance = fc.severe[i];
        weight = 3;
      } else if (fc.precip[i] >= MET_PRECIP_CHANCE && !(now & W_RAIN)) {
        cond = snowy ? 'snow' : 'rain';
        chance = fc.precip[i];
        weight = snowy ? 2 : 1.5;
      }
      if (!cond) continue;
      const score = (weight * chance) / 100 / (1 + 0.08 * fc.leads[s]);
      if (!best || score > best.score)
        best = { region: r, condition: cond, lead: fc.leads[s], chance, score };
      break;
    }
    if (fc.fog[r] >= MET_FOG_CHANCE && !(now & W_FOG)) {
      const tick = ctx.state.tick;
      let lead = 1;
      while (hourAt(tick + lead) !== FOG_START_HOUR) lead++;
      const score = fc.fog[r] / 100 / (1 + 0.08 * lead);
      if (!best || score > best.score) best = { region: r, condition: 'fog', lead, chance: fc.fog[r], score };
    }
  }
  return best;
}

function announceForecast(ctx: SimContext, info: MapInfo): void {
  const { state } = ctx;
  const ws = state.weather;
  for (const fs of state.factions) {
    if (!fs.alive) continue;
    const f = fs.id;
    if (state.tick - ws.metAt[f] < MET_INTERVAL) continue;
    const c = metCandidate(ctx, f, info);
    if (!c) continue;
    const key = `${c.region}:${c.condition}`;
    if (key === ws.metKey[f] && state.tick - ws.metAt[f] < 24) continue;
    ws.metAt[f] = state.tick;
    ws.metKey[f] = key;
    const hex = ctx.map.regions[c.region]?.hex ?? NO_HEX;
    emit(
      ctx,
      {
        kind: 'weatherForecast',
        region: c.region,
        hex,
        condition: c.condition,
        at: state.tick + c.lead,
        chance: c.chance,
      },
      [f],
    );
  }
}

/** Public notices when the ground changes character across the theatre (hysteresis). */
function announceRegimes(ctx: SimContext, s: Shares): void {
  const ws = ctx.state.weather;
  const notice = (change: WeatherChange) => emit(ctx, { kind: 'weatherChange', change, hex: NO_HEX }, 'all');
  const r = ws.regime;
  if (!r.frozen && s.frozen >= REGIME_SHARES.frozen[0]) {
    r.frozen = true;
    notice('frost');
  } else if (r.frozen && s.frozen < REGIME_SHARES.frozen[1]) {
    r.frozen = false;
    notice('thaw');
  }
  if (!r.mud && s.mud >= REGIME_SHARES.mud[0]) {
    r.mud = true;
    notice('mud');
  } else if (r.mud && s.mud < REGIME_SHARES.mud[1]) {
    r.mud = false;
    notice('firm');
  }
  if (!r.snow && s.snow >= REGIME_SHARES.snow[0]) {
    r.snow = true;
    notice('snow');
  } else if (r.snow && s.snow < REGIME_SHARES.snow[1]) r.snow = false;
}

// ---- system entry ---------------------------------------------------------------------------

/** Tick-order step 1: the weather of the new hour, the MET bulletin, the notices. */
export function updateWeather(ctx: SimContext): void {
  const { state, map, grid } = ctx;
  const ws = state.weather;
  if (!ws.live) return;
  const info = mapInfo(map, grid);
  const climate = CLIMATE[ws.season];
  announceRegimes(ctx, advance(ws, info, map, climate, state.tick));
  if (hourAt(state.tick) % FORECAST_INTERVAL === 0) {
    ws.forecast = computeForecast(ws, info, map, climate, state.config.seed, state.tick);
    announceForecast(ctx, info);
  }
}

// ---- dev: forced weather -------------------------------------------------------------------

/** Weather a dev session can be pinned to for visual checks (`?quickstart&weather=storm`). */
export type ForcedWeather = 'clear' | 'rain' | 'storm' | 'snow' | 'blizzard' | 'fog' | 'mud' | 'frost';
export const FORCED_WEATHER: readonly ForcedWeather[] = [
  'clear',
  'rain',
  'storm',
  'snow',
  'blizzard',
  'fog',
  'mud',
  'frost',
];

/** Id range of the pinned cells, so they are recognised and kept across hours. */
const FORCED_ID = 1_000_000;

/**
 * Pin the weather after the model's hour (dev only — sessions doing this are not replayable):
 * precipitation from a train of drifting systems that wrap around the map, fog at any hour,
 * mud or hard frost on the ground. Rules and views follow the pinned conditions.
 */
export function forceWeather(ws: WeatherState, map: GameMap, grid: HexGrid, kind: ForcedWeather): void {
  const info = mapInfo(map, grid);
  const wet = kind === 'rain' || kind === 'storm';
  const cold = kind === 'snow' || kind === 'blizzard' || kind === 'frost';
  if (cold) ws.temperature = Math.min(ws.temperature, -7);
  else if (ws.temperature < 3) ws.temperature = 8;
  if (wet || kind === 'snow' || kind === 'blizzard') {
    const heavy = kind === 'storm' || kind === 'blizzard';
    let pinned = ws.cells.filter((c) => c.id >= FORCED_ID);
    if (pinned.length === 0) {
      const w = ws.wind;
      const speed = Math.sqrt(w.x * w.x + w.y * w.y) || 1;
      const span = info.x1 - info.x0;
      pinned = [0, 1, 2].map((i) => ({
        id: FORCED_ID + i,
        x: info.x0 + (span * (i + 0.5)) / 3,
        y: (info.y0 + info.y1) / 2 + (i - 1) * 4,
        ux: -w.y / speed,
        uy: w.x / speed,
        major: (info.y1 - info.y0) * 0.75,
        minor: span / 4,
        vx: w.x,
        vy: w.y,
        peak: heavy ? 1 : 0.55,
        age: 500,
        life: 1000,
      }));
    }
    for (const c of pinned) {
      c.age = 500;
      const span = info.x1 - info.x0 + 2 * c.minor;
      if (c.x > info.x1 + c.minor) c.x -= span;
      if (c.x < info.x0 - c.minor) c.x += span;
      c.y = Math.min(info.y1, Math.max(info.y0, c.y));
    }
    ws.cells = pinned;
  } else if (kind !== 'fog') ws.cells = ws.cells.filter((c) => c.id < FORCED_ID);
  const precip = rasterPrecip(ws, info);
  for (let h = 0; h < grid.count; h++) {
    if (!info.land[h]) continue;
    const t = ws.temperature - info.lapse[h];
    let c = ws.conditions[h] & ~(W_RAIN | W_STORM | W_SNOWING | W_FOG);
    if (kind === 'clear') c = 0;
    const p = precip[h];
    if (p >= RAIN_MIN) {
      c |= W_RAIN;
      if (p >= STORM_MIN) c |= W_STORM;
      if (t <= SNOW_TEMP) c |= W_SNOWING;
    }
    if (kind === 'fog' && info.fogBase[h] >= 0) c |= W_FOG;
    if (kind === 'mud') {
      ws.wetness[h] = 0.85;
      c = (c & ~(W_FROZEN | W_SNOW)) | W_WET | (info.mudCapable[h] ? W_MUD : 0);
    }
    if (cold) {
      ws.snow[h] = Math.max(ws.snow[h], 0.5);
      ws.frost[h] = FROST_MAX;
      c = (c & ~(W_WET | W_MUD)) | W_FROZEN | W_SNOW;
    }
    ws.conditions[h] = c;
  }
}

/**
 * The weather on its own — the same model as a session's, without events — for tools that
 * fabricate views (render lab, dev fixtures). `step()` advances one hour.
 */
export class WeatherModel {
  readonly state: WeatherState;
  private readonly map: GameMap;
  private readonly info: MapInfo;
  private readonly seed: number;
  private readonly forced: ForcedWeather | null;
  private tick = 0;

  constructor(
    config: Pick<SessionConfig, 'seed' | 'season'>,
    map: GameMap,
    forced: ForcedWeather | null = null,
  ) {
    const grid = new HexGrid(map.width, map.height);
    this.map = map;
    this.seed = config.seed;
    this.forced = forced;
    this.info = mapInfo(map, grid);
    this.state = initWeather(config, map, grid, 0, true);
    if (forced) forceWeather(this.state, map, grid, forced);
  }

  step(): void {
    const climate = CLIMATE[this.state.season];
    this.tick++;
    advance(this.state, this.info, this.map, climate, this.tick);
    if (hourAt(this.tick) % FORECAST_INTERVAL === 0)
      this.state.forecast = computeForecast(this.state, this.info, this.map, climate, this.seed, this.tick);
    if (this.forced) forceWeather(this.state, this.map, this.info.grid, this.forced);
  }
}
